import {
  makeSelectedEvent,
  selectVoucherData,
} from 'app/pages/TicketSalePage/slice/selectors';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Ticket } from 'types/TicketsState';
import { formatDateAndTimeOnTimeZone } from 'utils/events';
import { buildVoucherPrice, getTaxPrice } from 'utils/tickets';
import nftIcon from '../../../../assets/common/nft-icon.svg';
import { EVENT_TYPE_WITH_CALENDAR } from 'utils/static/constants';
import TicketAvailabilityLabel from '../TicketAvailabilityLabel';

interface TicketGenericsProps {
  ticket: Ticket;
  displaySoldFromDate: boolean;
  displaySoldToDate: boolean;
  ticketHasDiscount: boolean;
  selectedDate: DateTime | undefined;
  ticketAvailableQuantity: number;
}

const TicketGenerics: React.FC<TicketGenericsProps> = ({
  ticket: {
    id,
    hasCalendar,
    nft_ticket,
    name,
    sold_to,
    sold_from,
    visibility,
    limit,
    total,
    seatsCategoryKey,
  },
  displaySoldFromDate,
  displaySoldToDate,
  ticketHasDiscount,
  selectedDate,
  ticketAvailableQuantity,
}) => {
  const { t, i18n } = useTranslation();

  const {
    type,
    dateTimeFormat: { dateFormat, timeFormat, timezone },
    timeInterval: { endsAt },
    checkoutConfiguration: { displayAvailability },
    tax_included,
    tax_rate,
  } = useSelector(makeSelectedEvent);

  const {
    voucherData: { discount, discount_percent, currency: voucherCurrency },
  } = useSelector(selectVoucherData);

  return (
    <div className="flex flex-col sm:flex-row">
      {nft_ticket ? (
        <img
          className="h-[56px] w-[56px] mr-[16px]"
          src={nftIcon}
          alt="nft-icon"
        />
      ) : null}
      <div className="flex flex-col justify-center">
        <h3 className="title-xs mb-[4px] max-w-[180px] md:max-w-[240px]">
          {name}
        </h3>
        {displaySoldToDate ? (
          <span className="p3-regular text-neutral-800">
            {t('ticketSale.saleEndOn')}{' '}
            {formatDateAndTimeOnTimeZone(
              (sold_to || endsAt) as string,
              dateFormat,
              timeFormat,
              timezone,
              t('common.at'),
              true,
            )}
          </span>
        ) : null}
        {displaySoldFromDate ? (
          <span className="p3-regular text-neutral-800">
            {t('ticketSale.saleStartOn')}{' '}
            {formatDateAndTimeOnTimeZone(
              sold_from as string,
              dateFormat,
              timeFormat,
              timezone,
              t('common.at'),
              true,
            )}
          </span>
        ) : null}
        {selectedDate && hasCalendar ? (
          <span className="p4-regular text-neutral-800">
            ({selectedDate.setLocale(i18n.language).toFormat('cccc, LLLL d')})
          </span>
        ) : null}
        <div className="flex gap-[8px] mt-[4px]">
          {visibility === 'private' ? (
            <p className="orange-label">{t('ticketSale.privateMessage')}</p>
          ) : null}
          {ticketHasDiscount ? (
            <p className="green-label">
              {/* for explanation of this logic scroll down to the bottom of the file */}
              {tax_included && !discount_percent
                ? buildVoucherPrice(
                    discount + getTaxPrice(discount, tax_rate.rate),
                    voucherCurrency,
                    discount_percent,
                  )
                : buildVoucherPrice(
                    discount,
                    voucherCurrency,
                    discount_percent,
                  )}{' '}
              {t('ticketSale.discountMessage')}
            </p>
          ) : null}
          {displayAvailability && type !== EVENT_TYPE_WITH_CALENDAR ? (
            // to do: lazy load this component after we start using Observer API
            <TicketAvailabilityLabel
              limit={limit}
              total={total}
              seatsCategoryKey={seatsCategoryKey}
              displaySoldFromDate={displaySoldFromDate}
            />
          ) : ticketAvailableQuantity <= 0 ? (
            <p className="red-label">{t('common.soldOut')}</p>
          ) : null}
          {/* this div should be placed at the bottom */}
          <div id={`bulk-option-save-amount-${id}`}></div>
        </div>
      </div>
    </div>
  );
};

export default TicketGenerics;

// Q: Why add the tax price to the discount when voucher code is numeric and non-percentage?
// A: Because the numeric discounts should be applied to the ticket price before tax even if "Include tax in displayed prices" is checked
// Example: ticket costs $200, tax is $10 and checkbox is checked => displayed price is $220
// If we apply a voucher code of $20 => the new ticket price will be $180 ( $200 - $20 ) => new tax is $18 => new displayed price should be $198
// The initial price was $220, the new price is $198 => the discount is $22. So that's why we add the tax price to the discount.

// This behaviour shouldn't be replicated for percentage discounts because they are relative, not absolute like numeric discounts.
// Example: ticket costs $200, tax is $10 and checkbox is checked => displayed price is $220
// If we apply a voucher code of 20% => the new ticket price will be $160 ( $200 - 20% ) => new tax is $16 => new displayed price should be $176
// The initial price was $220, the new price is $176 which is correct because 220% * 0.8 = 176
